/**
 * 에피소드 전역 리뷰 품질 이슈 태그의 한글 표시 라벨과 설명(제품 카피, FE 소유).
 *
 * 태그 어휘와 표시 순서는 `GET /v1/review-options` 의 `episode_quality_tags`(서버 SoT)가
 * 소유한다. 이 파일은 그 키에 한국어 라벨과 툴팁만 매핑한다. 정의되지 않은 키는 키 그대로
 * 또는 `null` 로 폴백한다. 그래서 서버가 새 태그를 먼저 보내도 화면이 깨지지 않는다.
 *
 * 데이터셋 상세의 에피소드 품질 롤업 표시도 같은 어휘를 쓴다. 저장 축은 라벨 매핑이고,
 * 데이터셋 축 표시는 이 태그들의 집계에서 계산하기 때문이다.
 */
export const EPISODE_QUALITY_TAG_LABELS: Record<string, { ko: string; tip: string }> = {
  manipulation_failed: {
    ko: "조작 실패",
    tip: "대상을 잡거나 놓지 못했거나, 대상이 밀리거나 벗어난 경우입니다. 최종 결과와 무관하게 붙입니다.",
  },
  collision: {
    ko: "충돌",
    tip: "작업 범위를 벗어난 부딪힘이 있는 경우입니다. 작업에 필요한 접촉은 충돌이 아닙니다.",
  },
  // `trajectory` 는 rules §11 이 영어로 고정한 공유 명사다. 진단 화면도 같은 표기를 쓴다.
  trajectory_anomaly: {
    ko: "trajectory 이상",
    tip: "trajectory가 급격히 바뀌거나 속도가 비정상인 경우입니다.",
  },
  incomplete: {
    ko: "작업 도중 종료",
    tip: "작업이 진행 중인 상태에서 기록이 끝난 경우입니다. 누가 끝냈는지는 따지지 않습니다.",
  },
  timeout: { ko: "시간 초과", tip: "명시된 제한 시간을 넘겨 끝난 경우입니다." },
  target_not_visible: {
    ko: "대상 안 보임",
    tip: "대상이 가려지거나 화면 밖으로 나가 보이지 않는 구간이 있는 경우입니다.",
  },
  // 조작이 아니라 카메라 구성의 결함이다. 어느 채널이 틀렸는지 판정하지 않는다.
  camera_setup_anomaly: {
    ko: "카메라 구성 이상",
    tip: "영상이 반전되었거나 채널의 짝·순서·반전이 잘못된 경우입니다.",
  },
  robot_fault: {
    ko: "로봇 결함",
    tip: "로봇 팔·그리퍼 자체의 이상이 확인된 경우입니다. 원인을 모르는 정지·떨림에는 붙이지 않습니다.",
  },
  // 영상 결함 4종. 조작이 아니라 카메라 영상 자체의 결함이다. 에피소드의 모든 카메라 영상이 대상이다.
  // 진단의 영상 정지 후보에는 로봇이 멈춰 기다린 구간도 들어간다. 그래서 정지 설명은 대기를 뺀다.
  video_freeze: {
    ko: "영상 정지",
    tip: "카메라 영상이 멈춰 같은 화면이 이어지는 구간이 있는 경우입니다. 로봇이 멈춰 기다린 구간은 해당하지 않습니다.",
  },
  video_brightness: {
    ko: "밝기 이상",
    tip: "카메라 영상이 너무 어둡거나 너무 밝은 구간이 있는 경우입니다.",
  },
  // 색상은 한 Dataset 안의 비교로 판정한다. 모든 에피소드의 색이 같으면 결함이 아니다.
  video_color: {
    ko: "색상 이상",
    tip: "같은 Dataset의 다른 에피소드와 카메라 색이 다른 경우입니다. 모든 에피소드의 색이 같으면 해당하지 않습니다.",
  },
  video_blur: {
    ko: "영상 흐림",
    tip: "카메라 영상이 흐려 대상을 알아보기 어려운 구간이 있는 경우입니다.",
  },

  // 더 이상 쓰지 않는 어휘. 서버가 보내지 않으므로 칩으로 표시되지 않는다. 어휘를 바꾸기 전에
  // 태그를 단 에피소드를 롤업이 영문 키로 표시하지 않도록 표시 문구만 남긴다.
  task_failed: { ko: "작업 실패", tip: "태스크가 목표 상태에 도달하지 못하고 끝난 경우입니다." },
  recovery: {
    ko: "복구 포함",
    tip: "실패 후 복구 동작이 포함된 경우입니다.",
  },
};

// 태그 키 → 한글 라벨. 정의되지 않은 키는 키 그대로 폴백한다. 서버가 새 태그를 먼저 보내도 깨지지 않는다.
export function episodeQualityTagLabel(tag: string): string {
  return EPISODE_QUALITY_TAG_LABELS[tag]?.ko ?? tag;
}

/**
 * 표시 문구 `한글(영어)`. 한 줄 텍스트로 쓰는 자리(필터 옵션, 롤업 행)의 기준 형식이다.
 *
 * 원문 키를 항상 함께 표시하는 이유는 두 가지다:
 * - 필터·집계 결과를 MCP·API 응답과 비교할 때 키가 비교 기준이다.
 * - 서버가 어휘를 늘리면 한글이 없는 태그가 섞인다. 그때도 표기 형식이 같다.
 * 문구가 없는 키는 괄호 없이 키만 표시한다. `new_tag(new_tag)` 는 정보를 주지 않는다.
 */
export function episodeQualityTagText(tag: string): string {
  const ko = EPISODE_QUALITY_TAG_LABELS[tag]?.ko;
  return ko === undefined ? tag : `${ko}(${tag})`;
}

// 태그 키 → 설명 툴팁. 없으면 null.
export function episodeQualityTagTip(tag: string): string | null {
  return EPISODE_QUALITY_TAG_LABELS[tag]?.tip ?? null;
}
